import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PersonAdd from "@mui/icons-material/PersonAdd";
import RemoveFilled from "@mui/icons-material/Remove";
import { Alert, Box, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { IssueCount } from "components/IssueCount";
import { useDueDateWarningsByUserId, useReviewerIssues } from "contexts/SubmissionFormContext";
import { Permission, SignatureType } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useTenant } from "hooks/useTenant";
import { compact } from "lodash-es";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { prettyPrintDate } from "util/dates";

import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { utcDueDateTooltip } from "./helpers/dueDateWarnings";
import { renderIssueMessage } from "./helpers/renderIssueMessage";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

type SignersDatagridProps = {
  signers: SubmissionReviewerSigner[];
  signatureType: SignatureType;
  handleAddSigner: () => void;
  handleRemoveSigner: (signer: SubmissionReviewerSigner) => void;
  submissionDueDate?: DateTime | null;
  disabled?: boolean;
};

export function SignersDatagrid({
  signers,
  signatureType,
  handleAddSigner,
  handleRemoveSigner,
  submissionDueDate,
  disabled = false,
}: SignersDatagridProps) {
  const issues = useReviewerIssues();
  const dueDateWarningsByUserId = useDueDateWarningsByUserId();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  const signerIssues = useMemo(
    () =>
      issues.filter((i) =>
        signers.some((s) => i.schemaStack?.some((schemaStack) => schemaStack.name === s.id))
      ),
    [issues, signers]
  );
  const bannerIssues = useMemo(
    () => signerIssues.filter((i) => i.key !== "dueDate"),
    [signerIssues]
  );
  const columns = useMemo(() => {
    const columns: GridColDef<SubmissionReviewerSigner>[] = [
      {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 0.65,
        sortable: false,
      },
    ];

    if (signatureType === SignatureType.Digital) {
      columns.push({
        field: "email",
        headerName: "Email",
        display: "flex",
        sortable: false,
        flex: 1,
      });
    }

    if (signatureType === SignatureType.WetInk) {
      columns.push({
        field: "address",
        headerName: "Address",
        display: "flex",
        sortable: false,
        flex: 1,
        valueGetter: (_, row) => {
          return compact([
            compact([row.streetAddress1, row.streetAddress2, row.city]).join(" "),
            compact([row.state, row.zip]).join(" "),
            row.country,
          ]).join(", ");
        },
      });
    }

    columns.push({
      field: "dueDate",
      display: "flex",
      // The warning under the date needs the room; Issues only holds a count.
      flex: 1.6,
      minWidth: 200,
      sortable: false,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500}>
            Due Date
          </Typography>
          <Tooltip placement="right" title={utcDueDateTooltip(DateTime.utc().startOf("day"))}>
            <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          </Tooltip>
        </Stack>
      ),
      renderCell: ({ row }) => {
        const errorMessage = row.id
          ? signerIssues.find(
              (i) => i.key === "dueDate" && i.schemaStack?.some((s) => s.name === row.id)
            )?.message
          : undefined;
        const warningMessage = row.id ? dueDateWarningsByUserId[row.id] : undefined;
        const message = errorMessage ?? warningMessage;
        const dateStr = row.dueDate ? prettyPrintDate(row.dueDate.toISO()) : "";

        return (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{dateStr}</Typography>
            {message && (
              <Tooltip title={message}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: errorMessage ? "error.main" : "warning.dark" }}
                >
                  {message}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        );
      },
    });
    if (signerIssues.length > 0) {
      columns.push({
        field: "issues",
        display: "flex",
        width: 72,
        headerName: "Issues",
        renderCell: ({ row }) => {
          const rowIssues = signerIssues.filter((i) => {
            return i.schemaStack?.some((s) => s.name === row.id);
          });
          return <IssueCount issueCount={rowIssues.length} />;
        },
      });
    }

    if (!disabled) {
      // Always add actions as the last column
      columns.push({
        field: "actions",
        type: "actions",
        headerName: "",
        width: 80,
        align: "right",
        sortable: false,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="delete"
            label="Delete"
            icon={<RemoveFilled />}
            onClick={() => {
              handleRemoveSigner(row);
            }}
          />,
        ],
      });
    }
    return columns;
  }, [signatureType, handleRemoveSigner, signerIssues, dueDateWarningsByUserId, disabled]);

  return (
    <Box>
      {/* Due date issues are rendered under the date itself, so the banner would repeat them. */}
      {bannerIssues.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bannerIssues.map((i, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(i.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      {disabled ? (
        <SubmissionFormDatagrid rows={signers} columns={columns} />
      ) : (
        <DataGridWithAddButton
          rows={signers}
          columns={columns}
          addButtonConfig={{
            buttonText: "Add Signer",
            icon: <PersonAdd />,
            colSpan: 3,
          }}
          onAdd={handleAddSigner}
          createEmptyRow={() => ({
            id: "empty",
            name: "",
            email: "",
            streetAddress1: "",
            streetAddress2: "",
            city: "",
            state: "",
            zip: "",
            country: "",
            order: 0,
            isStaff: false,
          })}
          gridProps={{
            onRowClick: handleAddSigner,
          }}
        />
      )}
    </Box>
  );
}
